import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { generateSolution, renderVideoFromSolution, getVideoStatus } from '../services/api';
import { authService } from '../services/authService';
import { ArrowLeft, Send, Image as ImageIcon, Camera, Sparkles, Video } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './PixelTutor.css';

const PixelTutorPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].pixel;
  const user = authService.getUser();
  
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [preferences, setPreferences] = useState({
    subject: '',
    grade: '',
    goal: ''
  });
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [videoPollingIntervals, setVideoPollingIntervals] = useState({});
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(videoPollingIntervals).forEach(interval => clearInterval(interval));
    };
  }, [videoPollingIntervals]);
  
  // Initialize with welcome message
  useEffect(() => {
    if (!onboardingComplete) {
      setMessages([{
        id: 1,
        type: 'pixel',
        content: t.welcome + '\n\n' + t.welcomeDesc,
        timestamp: new Date()
      }, {
        id: 2,
        type: 'pixel',
        content: t.aboutAnimLearn + '\n\n' + 
                 t.capability1 + '\n' +
                 t.capability2 + '\n' +
                 t.capability3 + '\n' +
                 t.capability4 + '\n' +
                 t.capability5,
        timestamp: new Date()
      }, {
        id: 3,
        type: 'pixel',
        content: t.readyMessage,
        timestamp: new Date()
      }]);
    }
  }, [t, onboardingComplete]);
  
  const handleOnboardingChoice = async (choice, step) => {
    // Add user choice message
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: choice,
      timestamp: new Date()
    }]);
    
    // Update preferences
    const prefKeys = ['subject', 'grade', 'goal'];
    setPreferences(prev => ({
      ...prev,
      [prefKeys[step]]: choice
    }));
    
    // Move to next step or complete
    if (step < 2) {
      setOnboardingStep(step + 1);
      
      // Add next question
      setTimeout(() => {
        const questions = [
          t.onboarding.step1Question,
          t.onboarding.step2Question,
          t.onboarding.step3Question
        ];
        
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'pixel',
          content: questions[step + 1],
          timestamp: new Date()
        }]);
      }, 500);
    } else {
      // Complete onboarding
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'pixel',
        content: t.onboarding.processing,
        timestamp: new Date()
      }]);
      
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now() + 2,
          type: 'pixel',
          content: t.onboarding.allSet + '\n\n' + t.onboarding.preferencesSet + '\n\n' + t.onboarding.readyToHelp,
          timestamp: new Date()
        }]);
        setOnboardingComplete(true);
      }, 1500);
    }
  };
  
  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setIsLoading(true);
    
    // Add thinking indicator
    const thinkingId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: thinkingId,
      type: 'pixel',
      content: t.chat.analyzing,
      isThinking: true,
      timestamp: new Date()
    }]);
    
    try {
      // Map grade to subject if not set
      const subjectMapping = {
        'Matematiikka': 'mathematics',
        'Math': 'mathematics',
        'Matematik': 'mathematics',
        'Kemia': 'chemistry',
        'Chemistry': 'chemistry',
        'Kemi': 'chemistry',
        'Fysiikka': 'physics',
        'Physics': 'physics',
        'Fysik': 'physics'
      };
      
      const detectedSubject = subjectMapping[preferences.subject] || 'chemistry';
      
      // Call NEW generate-solution API (fast, no video)
      const response = await generateSolution(
        currentInput,
        language,
        60, // Target duration
        detectedSubject
      );
      
      // Remove thinking indicator
      setMessages(prev => prev.filter(msg => msg.id !== thinkingId));
      
      // Add Pixel's response with solution ID
      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        type: 'pixel',
        content: response.structured_solution || response.transcript_markdown,
        solutionId: response.id,  // IMPORTANT: Store for video generation
        timestamp: new Date(),
        showActions: true
      }]);
      
      console.log('✅ Solution generated:', response.id);
      
    } catch (error) {
      console.error('Error:', error);
      
      // Remove thinking indicator
      setMessages(prev => prev.filter(msg => msg.id !== thinkingId));
      
      // Add error message
      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        type: 'pixel',
        content: t.errors.failedToLoad + '\n\n' + error.message,
        isError: true,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAction = (action, solutionId) => {
    if (action === 'createVideo') {
      handleCreateVideo(solutionId);
    } else {
      const actionMessages = {
        diveDeeper: 'I want to dive deeper into this topic',
        practice: 'Give me practice questions on this',
        similar: 'Show me a similar question',
        askNew: 'I want to ask something else'
      };
      
      setInput(actionMessages[action] || '');
    }
  };
  
  const handleCreateVideo = async (solutionId) => {
    if (!solutionId) {
      console.error('No solution ID provided');
      return;
    }
    
    setIsCreatingVideo(true);
    
    // Add "creating video" message
    const creatingId = Date.now();
    setMessages(prev => [...prev, {
      id: creatingId,
      type: 'pixel',
      content: '🎬 Creating your educational video...',
      isThinking: true,
      timestamp: new Date()
    }]);
    
    try {
      // Call NEW render-from-solution API
      const response = await renderVideoFromSolution(
        solutionId,
        'qm',    // Quality
        '16:9'   // Aspect ratio
      );
      
      // Remove "creating" message
      setMessages(prev => prev.filter(msg => msg.id !== creatingId));
      
      // Add success message
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'pixel',
        content: `✅ Video is being created! I'll notify you when it's ready.\n\n**Video ID:** ${response.video_id}\n**Status:** ${response.status}`,
        videoId: response.video_id,
        timestamp: new Date()
      }]);
      
      // Start polling for video status
      pollVideoStatus(response.video_id);
      
      console.log('✅ Video rendering started:', response.video_id);
      
    } catch (error) {
      console.error('Error creating video:', error);
      
      // Remove "creating" message
      setMessages(prev => prev.filter(msg => msg.id !== creatingId));
      
      // Add error message
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'pixel',
        content: `❌ Failed to create video: ${error.message}`,
        isError: true,
        timestamp: new Date()
      }]);
    } finally {
      setIsCreatingVideo(false);
    }
  };
  
  const pollVideoStatus = (videoId) => {
    // Clear any existing interval for this video
    if (videoPollingIntervals[videoId]) {
      clearInterval(videoPollingIntervals[videoId]);
    }
    
    let pollCount = 0;
    const maxPolls = 60; // Max 5 minutes (60 * 5s = 300s)
    
    const interval = setInterval(async () => {
      pollCount++;
      
      try {
        const status = await getVideoStatus(videoId);
        
        if (status.status === 'completed') {
          // Clear interval
          clearInterval(interval);
          setVideoPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[videoId];
            return newIntervals;
          });
          
          // Add "video ready" message
          setMessages(prev => [...prev, {
            id: Date.now(),
            type: 'pixel',
            content: `🎉 **Your video is ready!**\n\nYou can watch it in your [Videos page](/videos) or click below:`,
            videoUrl: status.video_url,
            videoId: videoId,
            timestamp: new Date(),
            showVideoLink: true
          }]);
          
          console.log('✅ Video completed:', videoId);
          
        } else if (status.status === 'failed') {
          // Clear interval
          clearInterval(interval);
          setVideoPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[videoId];
            return newIntervals;
          });
          
          // Add error message
          setMessages(prev => [...prev, {
            id: Date.now(),
            type: 'pixel',
            content: `❌ **Video generation failed**\n\nError: ${status.error_message}`,
            isError: true,
            timestamp: new Date()
          }]);
          
          console.error('❌ Video failed:', videoId, status.error_message);
        }
        
        // Stop polling after max attempts
        if (pollCount >= maxPolls) {
          clearInterval(interval);
          setVideoPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[videoId];
            return newIntervals;
          });
          
          console.warn('⏱️ Video polling timeout:', videoId);
        }
        
      } catch (error) {
        console.error('Error polling video status:', error);
      }
    }, 5000); // Poll every 5 seconds
    
    // Store interval
    setVideoPollingIntervals(prev => ({
      ...prev,
      [videoId]: interval
    }));
  };
  
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'user',
        content: 'I uploaded an image. Can you help me solve this?',
        image: event.target.result,
        timestamp: new Date()
      }]);
      
      setShowImageOptions(false);
      
      // TODO: Add OCR/image analysis
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'pixel',
        content: '📸 I can see your image! Please type out the question from the image, and I\'ll help you solve it step-by-step.',
        timestamp: new Date()
      }]);
    };
    reader.readAsDataURL(file);
  };
  
  const OnboardingOptions = ({ options, step }) => (
    <div className="onboarding-options">
      {options.map((option, index) => (
        <button
          key={index}
          onClick={() => handleOnboardingChoice(option, step)}
          className="onboarding-option"
        >
          {option}
        </button>
      ))}
    </div>
  );
  
  const ActionButtons = ({ solutionId }) => (
    <div className="action-buttons-section">
      <p className="action-title">{t.actions.title}</p>
      <div className="action-buttons">
        <button onClick={() => handleAction('diveDeeper', solutionId)} className="action-btn">
          {t.actions.diveDeeper}
        </button>
        <button onClick={() => handleAction('practice', solutionId)} className="action-btn">
          {t.actions.practice}
        </button>
        <button onClick={() => handleAction('similar', solutionId)} className="action-btn">
          {t.actions.similar}
        </button>
        <button 
          onClick={() => handleAction('createVideo', solutionId)} 
          className="action-btn action-btn-primary"
          disabled={isCreatingVideo}
        >
          <Video size={16} />
          {t.actions.createVideo}
        </button>
        <button onClick={() => handleAction('askNew', solutionId)} className="action-btn">
          {t.actions.askNew}
        </button>
      </div>
    </div>
  );
  
  const MessageBubble = ({ message }) => {
    const isPixel = message.type === 'pixel';
    
    return (
      <div className={`message-wrapper ${isPixel ? 'pixel-message' : 'user-message'}`}>
        <div className="message-content-wrapper">
          {/* Avatar */}
          {isPixel && (
            <div className="pixel-avatar">
              <Sparkles size={20} />
            </div>
          )}
          
          {/* Message Bubble */}
          <div className={`message-bubble ${isPixel ? 'pixel-bubble' : 'user-bubble'}`}>
            {message.image && (
              <img src={message.image} alt="Uploaded" className="message-image" />
            )}
            
            <div className="message-text">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
            
            {message.isThinking && (
              <div className="thinking-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
            
            {/* Video Link Button */}
            {message.showVideoLink && message.videoUrl && (
              <div className="video-link-container">
                <button 
                  onClick={() => navigate('/videos')}
                  className="video-link-button"
                >
                  <Video size={18} />
                  Watch Video
                </button>
              </div>
            )}
          </div>
          
          {!isPixel && (
            <div className="user-avatar">
              {user?.full_name?.charAt(0) || 'S'}
            </div>
          )}
        </div>
        
        {/* Timestamp */}
        <div className={`message-timestamp ${isPixel ? 'pixel-timestamp' : 'user-timestamp'}`}>
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
        
        {/* Action Buttons - Pass solutionId */}
        {message.showActions && message.solutionId && (
          <ActionButtons solutionId={message.solutionId} />
        )}
        
        {/* Onboarding Options */}
        {!onboardingComplete && message.id === messages[messages.length - 1]?.id && (
          <OnboardingOptions 
            options={[
              t.onboarding.step1Options,
              t.onboarding.step2Options,
              t.onboarding.step3Options
            ][onboardingStep]}
            step={onboardingStep}
          />
        )}
      </div>
    );
  };
  
  return (
    <div className="pixel-tutor-page">
      {/* Header */}
      <header className="pixel-header">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          <ArrowLeft size={20} />
          <span>{t.backToDashboard}</span>
        </button>
        
        <div className="pixel-header-title">
          <Sparkles size={24} className="pixel-icon" />
          <h1>{t.aiTutor}</h1>
        </div>
        
        <div className="pixel-status">
          <div className="status-indicator online"></div>
          <span>{t.status.online}</span>
        </div>
      </header>
      
      {/* Messages Area */}
      <div className="messages-container">
        <div className="messages-inner">
          {messages.map(message => (
            <MessageBubble key={message.id} message={message} />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Input Area */}
      {onboardingComplete && (
        <div className="input-area">
          <div className="input-container">
            {/* Image Upload Button */}
            <div className="image-upload-wrapper">
              <button 
                onClick={() => setShowImageOptions(!showImageOptions)}
                className="image-upload-button"
                disabled={isLoading}
              >
                <ImageIcon size={20} />
              </button>
              
              {showImageOptions && (
                <div className="image-options-popup">
                  <button onClick={() => cameraInputRef.current?.click()}>
                    <Camera size={18} />
                    {t.chat.takePhoto}
                  </button>
                  <button onClick={() => fileInputRef.current?.click()}>
                    <ImageIcon size={18} />
                    {t.chat.uploadImage}
                  </button>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden-input"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageUpload}
                className="hidden-input"
              />
            </div>
            
            {/* Text Input */}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={t.chat.placeholder}
              disabled={isLoading}
              className="message-input"
              rows={1}
            />
            
            {/* Send Button */}
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading}
              className="send-button"
            >
              <Send size={20} />
            </button>
          </div>
          
          <p className="input-hint">
            Press Enter to send • Shift+Enter for new line
          </p>
        </div>
      )}
    </div>
  );
};

export default PixelTutorPage;
